// The organisation's IdP connection for test and production (IT 6.3.1 to 6.3.3).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import { api, ApiError } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { Facts } from '../ui/Facts.tsx';
import { CheckboxField, ErrorSummary, TextArea, TextField } from '../ui/Fields.tsx';

type Config = {
  environment: 'test' | 'production';
  protocol: 'oidc' | 'saml';
  displayName: string;
  issuer: string;
  clientId: string;
  hasSecret: boolean;
  scopes: string;
  groupsClaim: string;
  personClaim: string;
  enabled: boolean;
  samlSsoUrl: string;
  samlCertValidTo: string | null;
  spCert: string | null;
};
type Data = {
  active: string;
  redirectUri: string;
  logoutUri: string;
  acsUrl: string;
  spMetadataUrl: string;
  configs: Config[];
};

const empty = (environment: 'test' | 'production'): Config => ({
  environment,
  protocol: 'oidc',
  displayName: '',
  issuer: 'https://',
  clientId: '',
  hasSecret: false,
  scopes: 'openid profile email',
  groupsClaim: 'groups',
  personClaim: 'email',
  enabled: true,
  samlSsoUrl: '',
  samlCertValidTo: null,
  spCert: null,
});

function IdpForm({
  initial,
  spMetadataUrl,
  onDone,
}: {
  initial: Config;
  spMetadataUrl: string;
  onDone: () => void;
}) {
  const env = initial.environment;
  const [c, setC] = useState(initial);
  const [secret, setSecret] = useState('');
  const [cert, setCert] = useState('');
  const saml = c.protocol === 'saml';
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const set = (patch: Partial<Config>) => setC((x) => ({ ...x, ...patch }));
  const save = useMutation({
    mutationFn: () =>
      api(`/admin/idp/${env}`, {
        method: 'POST',
        body: JSON.stringify({
          ...c,
          clientSecret: (!saml && secret) || undefined,
          samlIdpCert: (saml && cert) || undefined,
        }),
      }),
    onSuccess: () => {
      setSaved(true);
      setSecret('');
      setCert('');
      onDone();
    },
    onError: (e) =>
      setErrors(
        e instanceof ApiError && e.errors.length
          ? Object.fromEntries(e.errors.map((x) => [x.path, x.message]))
          : { displayName: sv.errors.server },
      ),
  });
  const test = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; message: string }>(`/admin/idp/${env}/test`, { method: 'POST' }),
  });
  const id = (k: string) => `idp-${env}-${k}`;
  return (
    <form
      className="form-block block"
      noValidate
      aria-labelledby={id('title')}
      onSubmit={(e) => {
        e.preventDefault();
        setSaved(false);
        const next: Record<string, string> = {};
        if (c.displayName.trim().length < 2)
          next.displayName = sv.forms.required(sv.idp.displayName);
        if (!c.clientId.trim()) next.clientId = sv.forms.required(sv.idp.clientId);
        setErrors(next);
        if (!Object.keys(next).length) save.mutate();
      }}
    >
      <h2 className="block-title" id={id('title')}>
        {sv.idp.sectionTitle[env]}
      </h2>
      <ErrorSummary errors={Object.entries(errors).map(([k, m]) => ({ id: id(k), message: m }))} />
      <TextField
        id={id('displayName')}
        label={sv.idp.displayName}
        value={c.displayName}
        onChange={(v) => set({ displayName: v })}
        error={errors.displayName}
        required
      />
      <fieldset className="field">
        <legend className="label">{sv.idp.protocol}</legend>
        <div className="choices">
          {(['oidc', 'saml'] as const).map((p) => (
            <label className="choice" key={p} htmlFor={id(`protocol-${p}`)}>
              <input
                type="radio"
                id={id(`protocol-${p}`)}
                name={id('protocol')}
                value={p}
                checked={c.protocol === p}
                onChange={() =>
                  set(
                    p === 'saml' && !c.clientId.startsWith('http')
                      ? { protocol: p, clientId: spMetadataUrl, issuer: '' }
                      : { protocol: p },
                  )
                }
              />
              <span>{sv.idp.protocols[p]}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <TextField
        id={id('issuer')}
        label={saml ? sv.idp.samlEntityId : sv.idp.issuer}
        help={saml ? sv.idp.samlEntityIdHelp : sv.idp.issuerHelp}
        value={c.issuer}
        onChange={(v) => set({ issuer: v })}
        error={errors.issuer}
        required
      />
      {saml ? (
        <>
          <TextField
            id={id('samlSsoUrl')}
            label={sv.idp.samlSsoUrl}
            help={sv.idp.samlSsoUrlHelp}
            value={c.samlSsoUrl}
            onChange={(v) => set({ samlSsoUrl: v })}
            error={errors.samlSsoUrl}
            required
          />
          <TextArea
            id={id('samlIdpCert')}
            label={sv.idp.samlIdpCert}
            help={sv.idp.samlIdpCertHelp(c.samlCertValidTo)}
            value={cert}
            onChange={setCert}
            error={errors.samlIdpCert}
            rows={4}
          />
          <TextField
            id={id('clientId')}
            label={sv.idp.spEntityId}
            help={sv.idp.spEntityIdHelp}
            value={c.clientId}
            onChange={(v) => set({ clientId: v })}
            error={errors.clientId}
            required
          />
          {initial.spCert ? (
            <div className="field">
              <span className="label">{sv.idp.spCert}</span>
              <span className="help">{sv.idp.spCertHelp}</span>
              <pre
                className="cert"
                data-testid={id('spCert')}
                tabIndex={0}
                role="region"
                aria-label={sv.idp.spCert}
              >
                {initial.spCert.trim()}
              </pre>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <TextField
            id={id('clientId')}
            label={sv.idp.clientId}
            value={c.clientId}
            onChange={(v) => set({ clientId: v })}
            error={errors.clientId}
            required
          />
          <TextField
            id={id('clientSecret')}
            label={sv.idp.clientSecret}
            help={sv.idp.clientSecretHelp(c.hasSecret)}
            value={secret}
            onChange={setSecret}
          />
          <TextField
            id={id('scopes')}
            label={sv.idp.scopes}
            value={c.scopes}
            onChange={(v) => set({ scopes: v })}
            error={errors.scopes}
          />
        </>
      )}
      <TextField
        id={id('groupsClaim')}
        label={sv.idp.groupsClaim}
        help={sv.idp.groupsClaimHelp}
        value={c.groupsClaim}
        onChange={(v) => set({ groupsClaim: v })}
      />
      <TextField
        id={id('personClaim')}
        label={sv.idp.personClaim}
        help={sv.idp.personClaimHelp}
        value={c.personClaim}
        onChange={(v) => set({ personClaim: v })}
      />
      <CheckboxField
        id={id('enabled')}
        label={sv.idp.enabled}
        checked={c.enabled}
        onChange={(v) => set({ enabled: v })}
      />
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={save.isPending}>
          {sv.idp.save(sv.idp.environments[env]!)}
        </button>
        <button
          className="btn"
          type="button"
          disabled={test.isPending}
          onClick={() => test.mutate()}
        >
          {sv.idp.test(sv.idp.environments[env]!)}
        </button>
      </div>
      {saved ? (
        <p className="notice" data-state="done" role="status">
          {sv.idp.saved}
        </p>
      ) : null}
      {test.data ? (
        <p className="notice" data-state={test.data.ok ? 'done' : 'bad'} role="status">
          {test.data.message}
        </p>
      ) : null}
    </form>
  );
}

export function IdpPage() {
  const h1 = usePage(sv.idp.title);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['idp'], queryFn: () => api<Data>('/admin/idp') });
  const d = q.data;
  return (
    <>
      <PageHead
        help="idp"
        crumbs={[{ to: '/admin', label: sv.adminHome.title }, { label: sv.idp.title }]}
        title={sv.idp.title}
        titleRef={h1}
      />
      <p>{sv.idp.intro}</p>
      {d ? (
        <>
          <p>{sv.idp.active(sv.idp.environments[d.active] ?? d.active)}</p>
          <Facts
            facts={[
              {
                key: 'redirect',
                label: sv.idp.redirect,
                value: <code className="url">{d.redirectUri}</code>,
              },
              {
                key: 'logout',
                label: sv.idp.logout,
                value: <code className="url">{d.logoutUri}</code>,
              },
              { key: 'acs', label: sv.idp.acs, value: <code className="url">{d.acsUrl}</code> },
              {
                key: 'spMetadata',
                label: sv.idp.spMetadata,
                value: <code className="url">{d.spMetadataUrl}</code>,
              },
            ]}
          />
          {(['test', 'production'] as const).map((env) => (
            <IdpForm
              key={env}
              initial={d.configs.find((c) => c.environment === env) ?? empty(env)}
              spMetadataUrl={d.spMetadataUrl}
              onDone={() => void qc.invalidateQueries({ queryKey: ['idp'] })}
            />
          ))}
        </>
      ) : (
        <p role="status">{sv.app.loading}</p>
      )}
    </>
  );
}
